import { LabelId, type UserCommunication } from '@this+that/models';
import log from 'loglevel';

import { DraftProbeStatus } from '../../enums';
import { ComposeSnapshotSyncBlockedReason } from '../../enums/composeSnapshotSyncBlockedReason';
import {
  type DraftProbeResult,
  probeDraftVersion,
} from '../../hooks/UserCommunications/draft/probeDraftVersion';
import { resolveDraftAccount } from '../../hooks/UserCommunications/draft/resolveEmailAccount';
import { resolveServerRowId } from '../../hooks/UserCommunications/draft/resolveServerRowId';
import type { ComposeSnapshot } from '../../models/composeSnapshot';
import { useUserCommunicationsStore } from '../../store';
import {
  collectDraftIds,
  useComposeSnapshotIndexStore,
} from '../../store/ComposeSnapshotIndex';
import { useIntegrationsStore } from '../../store/Inbox/Integrations';
import {
  deleteComposeSnapshotsByDraftId,
  getComposeSnapshot,
  listDirtyComposeSnapshots,
  putComposeSnapshot,
} from '../../store/utils/composeSnapshotCache';
import {
  COMPOSE_SNAPSHOT_SWEEP_MAX_PROBES,
  COMPOSE_SNAPSHOT_SYNC_LOCK_NAME,
} from '../constants/drafts';
import { handleSelectDraftReqType } from '../handleSelectDraftReqType';

const LOG_MESSAGES = {
  SWEEP_FAILED: 'Orphan compose snapshot sweep failed',
  ROW_LOOKUP_FAILED: 'Server row lookup of a probed draft failed',
} as const;

export const collectLoadedDraftIds = (): Set<string> => {
  const { unconfirmedDrafts, labels, ...slices } =
    useUserCommunicationsStore.getState();
  const loaded = [
    slices.primary,
    slices.promotions,
    slices.social,
    slices.updates,
    slices.forums,
    slices.draft,
    slices.sent,
    slices.spam,
    slices.trash,
    slices.all,
    ...Object.values(labels),
  ].flat();
  const ids = new Set(Object.keys(unconfirmedDrafts));
  loaded.forEach((uc) => collectDraftIds(uc).forEach((id) => ids.add(id)));
  return ids;
};

const isSweepCandidate = (
  snapshot: ComposeSnapshot,
  excludedDraftIds: ReadonlySet<string>,
): boolean => {
  const { liveSessionIds, syncingSessionIds, verifiedDraftIds } =
    useComposeSnapshotIndexStore.getState();
  return Boolean(
    snapshot.draftId &&
      !liveSessionIds[snapshot.sessionId] &&
      !syncingSessionIds[snapshot.sessionId] &&
      !verifiedDraftIds[snapshot.draftId] &&
      !excludedDraftIds.has(snapshot.draftId),
  );
};

export const pickSweepDraftIds = (
  snapshots: readonly ComposeSnapshot[],
  excludedDraftIds: ReadonlySet<string>,
  limit: number,
): string[] => {
  const draftIds = new Set<string>();
  for (const snapshot of snapshots) {
    if (snapshot.draftId && isSweepCandidate(snapshot, excludedDraftIds)) {
      draftIds.add(snapshot.draftId);
    }
  }
  return [...draftIds].slice(0, limit);
};

export interface SweepOptions {
  fetchServerRow?: (rowId: string) => Promise<UserCommunication | undefined>;
}

const resolveSnapshotAccount = (snapshot: ComposeSnapshot) => {
  const { currentIntegrationAccount, integrationAccounts } =
    useIntegrationsStore.getState();
  return currentIntegrationAccount
    ? resolveDraftAccount(
        snapshot.fromEmail,
        currentIntegrationAccount,
        integrationAccounts ?? [],
        { strict: true },
      )
    : null;
};

const probeSnapshotDraft = (
  snapshot: ComposeSnapshot,
): Promise<DraftProbeResult> => {
  const account = resolveSnapshotAccount(snapshot);
  return probeDraftVersion({
    draftId: snapshot.draftId as string,
    account,
    draftType: handleSelectDraftReqType(snapshot.handler),
  });
};

const liveSessionIdList = (): string[] =>
  Object.keys(useComposeSnapshotIndexStore.getState().liveSessionIds);

// Re-read first: the row may have been synced, claimed or replaced while the probe ran.
const seedExistingSnapshot = async (
  userId: string,
  sessionId: string,
  draftVersion: string,
): Promise<void> => {
  const { liveSessionIds, syncingSessionIds } =
    useComposeSnapshotIndexStore.getState();
  const current = await getComposeSnapshot(userId, sessionId);
  if (!current || liveSessionIds[sessionId] || syncingSessionIds[sessionId]) {
    return;
  }
  const wasNotFoundBlock =
    current.syncBlockedReason === ComposeSnapshotSyncBlockedReason.NOT_FOUND;
  if (current.draftVersion && !wasNotFoundBlock) {
    return;
  }
  await putComposeSnapshot({
    ...current,
    draftVersion: current.draftVersion ?? draftVersion,
    syncBlockedReason: wasNotFoundBlock ? undefined : current.syncBlockedReason,
  });
};

// Gmail keeps serving a draft the user trashed (and updating it revives it), so only the server row's TRASH label tells it is gone.
const isTrashedOnServer = async (
  representative: ComposeSnapshot,
  draftId: string,
  draftVersion: string,
  { fetchServerRow }: SweepOptions,
): Promise<boolean> => {
  const platform = resolveSnapshotAccount(representative)?.platform;
  if (!fetchServerRow || !platform) {
    return false;
  }
  try {
    const row = await fetchServerRow(
      resolveServerRowId(platform, draftId, draftVersion),
    );
    return Boolean(row?.labelIds?.includes(LabelId.TRASH));
  } catch (error) {
    log.warn(LOG_MESSAGES.ROW_LOOKUP_FAILED, error);
    return false;
  }
};

const applyProbeResult = async (
  userId: string,
  draftId: string,
  result: DraftProbeResult,
  snapshots: readonly ComposeSnapshot[],
  options: SweepOptions,
): Promise<void> => {
  if (result.status === DraftProbeStatus.NOT_FOUND) {
    await deleteComposeSnapshotsByDraftId(userId, draftId, liveSessionIdList());
    return;
  }
  if (result.status !== DraftProbeStatus.EXISTS) {
    return;
  }
  const representative = snapshots.find((s) => s.draftId === draftId);
  if (
    representative &&
    (await isTrashedOnServer(
      representative,
      draftId,
      result.draftVersion,
      options,
    ))
  ) {
    await deleteComposeSnapshotsByDraftId(userId, draftId, liveSessionIdList());
    return;
  }
  useComposeSnapshotIndexStore.getState().markDraftVerified(draftId);
  for (const snapshot of snapshots) {
    if (snapshot.draftId === draftId) {
      await seedExistingSnapshot(
        userId,
        snapshot.sessionId,
        result.draftVersion,
      );
    }
  }
};

const sweepSerially = async (
  userId: string,
  options: SweepOptions,
): Promise<void> => {
  const snapshots = await listDirtyComposeSnapshots(userId);
  const draftIds = pickSweepDraftIds(
    snapshots,
    collectLoadedDraftIds(),
    COMPOSE_SNAPSHOT_SWEEP_MAX_PROBES,
  );
  for (const draftId of draftIds) {
    const representative = snapshots.find((s) => s.draftId === draftId);
    if (!representative || !navigator.onLine) {
      return;
    }
    const result = await probeSnapshotDraft(representative);
    await applyProbeResult(userId, draftId, result, snapshots, options);
  }
};

// Resolves false when another tab holds the lock or locks are unavailable.
export const sweepOrphanSnapshots = async (
  userId: string,
  options: SweepOptions = {},
): Promise<boolean> => {
  if (!navigator.locks) {
    return false;
  }
  try {
    return await navigator.locks.request(
      COMPOSE_SNAPSHOT_SYNC_LOCK_NAME,
      { ifAvailable: true },
      async (lock) => {
        if (!lock) {
          return false;
        }
        await sweepSerially(userId, options);
        return true;
      },
    );
  } catch (error) {
    log.warn(LOG_MESSAGES.SWEEP_FAILED, error);
    return false;
  }
};
